import { useMemo } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useClients } from './useClients';

export interface SearchContext {
  type: 'client' | 'global';
  clientId?: string;
  clientName?: string;
}

/**
 * Hook to detect the current page context for search scoping
 * Returns client context when on a client-related page, otherwise null (global)
 */
export function useSearchContext(): SearchContext | null {
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { clients } = useClients();

  const context = useMemo(() => {
    // Check URL params first (client detail page, analysis page, video page)
    // Routes: /clients/:id, /clients/:id/analysis/:analysisId, /clients/:id/analysis/:analysisId/video
    if (params.id && location.pathname.startsWith('/clients/')) {
      const client = clients?.find(c => c.id === params.id);
      return {
        type: 'client' as const,
        clientId: params.id,
        clientName: client?.name,
      };
    }

    // Check query params for filtered pages (projects, contracts, meetings)
    const clientIdFromQuery = searchParams.get('client');
    if (clientIdFromQuery && clientIdFromQuery !== 'all') {
      const client = clients?.find(c => c.id === clientIdFromQuery);
      return {
        type: 'client' as const,
        clientId: clientIdFromQuery,
        clientName: client?.name,
      };
    }

    // No client context - global search
    return null;
  }, [location.pathname, params.id, searchParams, clients]);

  return context;
}
